/*
 * Date: 2025
 * Description: Bot interactions pages for script execution and handling
 * Author: Philippe Leroux @ skitsc
 */

//Modules
import { Box, Button, Collapse, Grid, IconButton, Modal, Paper, Typography } from "@mui/material"
import { ReactElement, useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

//Components
import Tbl from "../components/table/main.table"
import PromptBody from "../components/modal/prompt.body"
import FormBody from "../components/modal/form.body"
import LogBox from "../components/container/active.log.box"
import ArchiveBox from "../components/container/archive.box"

//Interfaces
import { i_archive, i_runs } from "../interfaces/items.interface"
import { i_modal_form_props, i_promise, i_prompt_modal_props, i_script_entry, i_script_errors, i_script_query, i_socket_response, i_table_props, i_tbl_header } from "../interfaces/utility.interface"
import { t_display, t_transition_event } from "../types/types"
import { i_active_log_props, i_archive_box_props } from "../interfaces/display.interfaces"

//Utility
import { handleFetch } from "../api/fetch"
import { timestampToDate } from "../utils/utility"

//Constant
import { default_run, emptyScript , emptyScriptErrors, eventsArr } from "../utils/constant"

//Styles
import { header_row } from "../styles/tbl.styles"

//Context
import { ThemeContext } from "../context/context"
import { useSocketEvent } from "../context/socket.context"
import { useAlert } from "../context/alert.context"

//Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const Bot = () : ReactElement => {
    const { mode , } = useContext(ThemeContext)
    const [ data , setData ] = useState<i_runs[]>([])
    const [ archives , setArchives ] = useState<i_archive[]>([])
    const [ targetArchive , setTargetArchive ] = useState<i_archive | null>(null)
    const [ selectedArchive , setSelectedArchive ] = useState<i_archive | null>(null)
    const [ archiveLoading , setArchiveLoading ] = useState<boolean>(false)
    const [ open , setOpen ] = useState<boolean>(false)
    const [ hideTop , setHideTop ] = useState<boolean>(true)
    const [ hideMid , setHideMid ] = useState<boolean>(true)
    const [ hideBottom , setHideBottom ] = useState<boolean>(true)
    const [ cursor , setCursor ] = useState<number>(0)
    const [ selectedCursor , setSelectedCursor ] = useState<number | null>(null)
    const [ scripts , setScripts ] = useState<i_script_entry[]>([])
    const [ form , setForm ] = useState<i_script_entry>(emptyScript)
    const [ errors , setErrors ] = useState<i_script_errors>(emptyScriptErrors)
    const [ loading, setLoading ] = useState<boolean>(false)
    const [ target , setTarget ] = useState<i_runs>(default_run)
    const [ channel ] = useState<string>('bot_action')
    const [ runChannel ] = useState<string>('run')
    const [ modalType , setModalType ] = useState<t_transition_event>('update')
    const [ event , setEvent ] = useState<'archive' | 'run'>('archive')

    //Hooks
    const { showTopAlert , closeAlert } = useAlert()

    const nav = useNavigate()

    useSocketEvent(channel , (output : i_socket_response ) => {
        if(output.type === 'Add'){
            const find = scripts.find(( item : i_script_entry ) => item.pid === output.item.pid)
            if(!find) setScripts([...scripts, output.item])
        }
        if(output.type === 'Delete'){
            const newScripts = scripts.filter(( item : i_script_entry ) => item.pid!== output.item.pid)
            
            setScripts(newScripts)
        }
        if(output.type === 'Info'){
            setScripts((prevData) =>
                prevData.map((prev_item) => {
                    if(prev_item.pid === output.item.pid){
                        const msgArr = prev_item.data;
                        const isCountPresent = msgArr.some(msg => msg.count === output.item.count);
                        if (!isCountPresent) {
                            msgArr.push({ msg: output.item.msg, event: output.item.event, time: output.item.time, count: output.item.count });
                        }
                        return { ...prev_item, data: msgArr };
                    }
                    return prev_item;
                })
            );
        }

    })
    useSocketEvent(runChannel , (output : i_socket_response ) => {
        if(output.type === 'Add'){
            const find = data.find(( item : i_runs ) => item._id === output.item._id)
            if(!find) setData([output.item, ...scripts]);
        }
        if (output.type === 'Info') {
            let updatedData = [...data];
            for (const newItem of output.item as i_runs[]) {
                const index = updatedData.findIndex(item => item._id === newItem._id);
                if (index !== -1) {
                    updatedData[index] = { ...updatedData[index], ...newItem };
                } else {
                    updatedData.push(newItem);
                }
            }
            setData(updatedData);
        }
        if(output.type === 'Update'){
            setData((prevData) => prevData.map((prev_item) => {
                if(prev_item._id === output.item._id){
                    return output.item 
                }else{
                    return prev_item;
            }}));   
        }
        if(output.type === 'Delete'){
            const cleanRuns = data.filter(( item : i_runs ) => item._id!== output.item._id)
            setData(cleanRuns)
        }
    })
    useEffect(() => {
        const getRuns = async() => {
            setLoading(true)
            // This would need to be paginated or filtered someday
            const res = await handleFetch('/runs' , 'GET' , true , null)
            setLoading(false)
            if(res.type === 'Success'){
                setData(res.data)
                return
            }
            if(res.type === 'Unauthorized'){
                nav('/login')
                return
            }else{
                showTopAlert(res)
            }
        }
        getRuns()
    },[])
    useEffect(() => {
        const findSelected = scripts.find(( item : i_script_entry ) => item.pid === selectedCursor)
        if(!findSelected){
            setSelectedCursor(null)
            return
        }
    },[scripts])
    useEffect(() => {
        const getScripts = async() => {
            setLoading(true)
            const body : i_script_query = { event : 'lookup' , type : 'default' , timestamp : 0 }
            const res : i_promise = await handleFetch('/execute/script' , 'POST' , true , body)
            setLoading(false)
            if(res.type === 'Success'){
                setScripts(res.data)
                return
            }
            if(res.type === 'Unauthorized'){
                nav('/login')
                return
            }else{
                showTopAlert(res)
            }
        }
        getScripts()
    },[])
    useEffect(() => {
        const getScriptLogs = async() => {
            setArchiveLoading(true)
            const res = await handleFetch('/archives' , 'GET' , true , null)
            if(res.type === 'Success'){
                setArchives(res.data)
            }
            if(res.type === 'Unauthorized'){
                nav('/login')
                return
            }
            setArchiveLoading(false)
        }
        if(!hideBottom)getScriptLogs()
    },[hideBottom])
    useEffect(() => {
        if(errors.type !== '' && form.type !== 'default') setErrors({...errors, type : '' })
    },[errors , form])
    const handleArchiveSelection = async( archive : i_archive , type : t_display) => {
        if(type === 'delete'){
            setModalType(type)
            setEvent('archive')
            setTargetArchive(archive) 
            setOpen(true)
        }
        if(type === 'view'){
            const res = await handleFetch("/archive/"+archive.file , 'GET' , true , null)
            if(res.type === 'Success'){
                const formatedRes = { ...archive, row : res.data }
                setSelectedArchive(formatedRes)
            }
            if(res.type === 'Unauthorized'){
                nav('/login')
                return
            }
        }
    }
    const handleRunCallback = async(run : i_runs , type : t_display) => {
        setTarget(run)
        if(type === 'delete' || type === 'disable'){
            setModalType(type)
            setEvent('run')
        }
        setOpen(true)
    }
    const handleScriptCallback = async(script : i_script_entry , type : t_display) => {
        if(type === 'delete'){
            const body = { event : 'kill' , type : script.type , timestamp : 0 }
            await handleFetch("/execute/script" , 'POST' , true , body)
        }
    }
    const handleAddCallback = async() => {
        if(form.type === 'default'){
            setErrors({...errors, type : 'Select a script'})
            return
        }
        const body : i_script_query = { event : 'start' , type : form.type , timestamp : 0 , }
        const res : i_promise = await handleFetch('/execute/script' , 'POST' , true , body)
        if(res.type === 'Unauthorized'){
            nav('/login')
            return
        }
        if(res.type === 'Success'){
            setOpen(false)
            setForm(emptyScript)
            setScripts([...scripts, res.data])
        }else{
            showTopAlert(res)
        }
    }
    const handlePromptCallback = async() => {
        const method = modalType === 'delete'? 'DELETE' : 'PATCH'
        const item = event === 'run' ? target : targetArchive?.file
        const res = await handleFetch("/"+event+'/'+item  , method , true , null)
        if(res.type === 'Unauthorized'){
            nav('/login')
            return
        }
        if(res.type === 'Success'){
            if(event === 'archive'){
                if(selectedArchive && selectedArchive.file === targetArchive?.file){
                    setTargetArchive(null)
                }
                setArchives(archives.filter(( item : i_archive ) => item.file!== targetArchive?.file))
                setOpen(false)
            }
            if(event === 'run'){
                setData(data.filter(item => item._id!== target._id))
                setOpen(false)
            }
        }
        showTopAlert(res)
    }
    const handleAddScript = () => {
        setModalType('update')
        setOpen(true)
    }
    const renderTitlePrompt = () : string => {
        if(modalType === 'delete'){
            if(event === 'archive'){
                return 'Deleting this archive will delete all the related logs for '+ targetArchive?.type + ' on : ' + timestampToDate(Number(targetArchive?.timestamp), 'long')+'?'
            }
            if(event === 'run'){
                return 'Deleting this run will delete all the related parts data for '+ target.event + '?'
            }
        }
        if(modalType === 'disable'){
            return 'Disabling this run will prevent it from being triggered again ' + target.event + '?'
        }
        return 'Please set a title for this action.'
    }
    const clearArchiveSelection = () => {
        setSelectedArchive(null)
    }
    const scriptHeaders : i_tbl_header[] = [
        { value : "Date Initiated" , css : { ...header_row, } , portion : 5 , },
        { value : "Name" , css : { ...header_row } , portion : 5 },
        { value : "Action" , css : { ...header_row } , portion : 2 },
    ]
    const runsHeaders : i_tbl_header[] = [
        { value : "Date added" , css : { ...header_row, } , portion : 3 , },
        { value : "Name" , css : { ...header_row } , portion : 3 },
        { value : "Index" , css : { ...header_row } , portion : 2 },
        { value : "Date ended" , css : { ...header_row } , portion : 2 },
        { value : "Action" , css : { ...header_row } , portion : 2 },
    ]
    const tbl_props : i_table_props = {
        data : data,
        loading : loading,
        headers : runsHeaders,
        row_model : 'runs',
        callback : handleRunCallback,
        closeAlert : () => closeAlert,
        title : 'Script Executions',
    }
    const script_tbl_props : i_table_props = {
        data : scripts,
        loading : loading,
        headers : scriptHeaders,
        row_model : 'scripts',
        callback : handleScriptCallback,
        closeAlert : () => closeAlert,
        title : 'No scripts running at the moment',
    } 
    const modalProps : i_modal_form_props = {
        open : open,
        title : 'Add a script',
        form : form,
        select_arr : eventsArr,
        setForm: setForm,
        callback : handleAddCallback,
        handleClose : () => setOpen(false),
        loading : loading,
        mode : mode,
        errors : errors,
        type : 'script'
    }
    const activeLogBoxProps : i_active_log_props = {
        data : scripts,
        selected : selectedCursor,
        setSelected : setSelectedCursor,
        cursor : cursor,
    }
    const archiveTblProps : i_table_props = {
        data : archives,
        loading : archiveLoading,
        headers : scriptHeaders,
        row_model : 'archives',
        callback : handleArchiveSelection,
        closeAlert : () => closeAlert,
        title : 'Archives',
    }
    const archiveBoxProps : i_archive_box_props = { 
        table : archiveTblProps,
        selected : selectedArchive,
        loading : archiveLoading,
        clear : clearArchiveSelection,
    }
    const promptBodyProps : i_prompt_modal_props = {
        handleClose : () => setOpen(false),
        title : renderTitlePrompt(),
        loading : loading,
        open : open,
        type : modalType,
        callback : handlePromptCallback,
        mode : mode
    }
    const titleStyle = { textAlign : 'center' , textDecoration : 'underline' , fontWeight : 800 }
    const subTitleStyle = { fontSize : 24 , textDecoration : 'underline' , fontWeight : 700 }
    const iconStyle = { fontSize : 30 , cursor : 'pointer' }
    const paperBoxStyle = { display : 'flex' , alignItems : 'center', marginLeft : 6}
    const paperStyle = { padding : 2 , margin : 3 , marginBottom : '2vh'}
    return (
        <Box sx={{height : '100%'}}>
                <Paper sx={paperStyle} elevation={4}>
                    <Box sx={paperBoxStyle}>
                        <Typography variant="h6" sx={subTitleStyle}>Run's and active script management..</Typography>
                        <IconButton onClick={() => setHideTop(hideTop ? false : true)}>{hideTop ? <ExpandLessIcon sx={iconStyle}/> : <ExpandMoreIcon sx={iconStyle}/>}</IconButton>
                    </Box>
                    <Collapse in={hideTop} timeout="auto">
                        <Grid container p={2}>
                            <Grid size={6} >
                                <Box sx={{ marginLeft : '4%' , marginRight : '2%'}}>
                                    <Typography variant="h6" sx={[titleStyle , { height : 36 } ]}>Runs list</Typography>
                                    <Box sx={{ minHeight : '500px' , overflowY : 'auto' , maxHeight : '25vh' }}>
                                        <Tbl {...tbl_props} />
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid size={6}>
                            <Box sx={{ marginRight : '4%' , marginLeft : '2%'}}>
                                <Box sx={{ display : 'flex' , justifyContent : 'center'}}>
                                    <Typography variant="h6" sx={titleStyle}>Active scripts</Typography>     
                                    <Button variant="contained" sx={{ marginLeft : 'auto'}} onClick={() => handleAddScript()}> Add a script </Button>                  
                                </Box>
                                <Box sx={{ minHeight : '500px', overflowY : 'auto' , maxHeight : '25vh' }}>
                                    <Tbl {...script_tbl_props} />
                                </Box>
                            </Box>
                            </Grid>
                        </Grid>
                    </Collapse>
                </Paper>
                <Paper elevation={4} sx={paperStyle}>
                    <Box sx={paperBoxStyle}>
                        <Typography variant="h6" sx={titleStyle}>Live Script execution Logs</Typography>
                        <IconButton onClick={() => setHideMid(hideMid ? false : true)}>{hideMid ? <ExpandLessIcon sx={iconStyle}/> : <ExpandMoreIcon sx={iconStyle}/>}</IconButton>
                    </Box>
                    <Collapse in={hideMid} timeout="auto">
                        <Grid size={12} sx={{ marginLeft : '3%' , marginRight : '3%'}}>
                            <LogBox {...activeLogBoxProps}/>
                        </Grid>
                    </Collapse>
                </Paper>

                <Paper elevation={4} sx={paperStyle}>
                    <Box sx={paperBoxStyle}>
                        <Typography variant="h6" sx={titleStyle}>Execution logs archives</Typography>
                        <IconButton onClick={() => setHideBottom(hideBottom ? false : true)}>{hideBottom ? <ExpandLessIcon sx={iconStyle}/> : <ExpandMoreIcon sx={iconStyle}/>}</IconButton>
                    </Box>
                    <Collapse in={!hideBottom} timeout="auto">
                        <Grid size={12} sx={{ marginLeft : '3%' , marginRight : '3%'}}>
                            <ArchiveBox {...archiveBoxProps} />
                        </Grid>
                    </Collapse>
                </Paper>
            <Modal open={open} onClose={() => setOpen(false)} children={modalType === 'update' ? <FormBody {...modalProps}/> : <PromptBody {...promptBodyProps} />}/>
        </Box>
    )
}


export default Bot